import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Calculator, Zap, CheckCircle2, TrendingUp } from 'lucide-react';

interface CalculationStats {
  calculationsPerformed: number;
  largestDataset: number;
  totalTime: number;
  averageTime: number;
  lastMethod: string;
}

interface StatisticsResult {
  dataset: number[];
  method: string;
  count: number;
  processingTime: number;
  timestamp: Date;
  descriptive?: {
    mean: number;
    median: number;
    mode: number[];
    range: number;
    min: number;
    max: number;
    q1: number;
    q3: number;
    iqr: number;
  };
  variability?: {
    variance: number;
    standardDeviation: number;
    coefficientOfVariation: number;
    meanAbsoluteDeviation: number;
  };
  distribution?: {
    skewness: number;
    kurtosis: number;
    isNormal: boolean;
    outliers: number[];
  };
  correlation?: {
    dataset2: number[];
    coefficient: number;
    determination: number;
    strength: string;
  };
  regression?: {
    slope: number;
    intercept: number;
    equation: string;
    rSquared: number;
  };
  frequencies?: { [key: string]: number };
}

const StatisticsCalculator: React.FC = () => {
  const [inputData, setInputData] = useState<string>('1, 2, 3, 4, 5, 6, 7, 8, 9, 10');
  const [inputData2, setInputData2] = useState<string>('');
  const [analysisMethod, setAnalysisMethod] = useState<'descriptive' | 'variability' | 'distribution' | 'correlation' | 'regression'>('descriptive');
  const [showDescriptive, setShowDescriptive] = useState<boolean>(true);
  const [showVariability, setShowVariability] = useState<boolean>(false);
  const [showDistribution, setShowDistribution] = useState<boolean>(false);
  const [showFrequencies, setShowFrequencies] = useState<boolean>(false);
  const [decimalPlaces, setDecimalPlaces] = useState<string>('2');
  const [result, setResult] = useState<StatisticsResult | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<CalculationStats>({
    calculationsPerformed: 0,
    largestDataset: 0,
    totalTime: 0,
    averageTime: 0,
    lastMethod: ''
  });
  const [notification, setNotification] = useState<string>('');
  const [history, setHistory] = useState<StatisticsResult[]>([]);

  // Helper functions for statistical calculations
  const parseData = (data: string): number[] => {
    return data.split(/[,\s\n\t]+/)
      .map(val => parseFloat(val.trim()))
      .filter(val => !isNaN(val));
  };

  const calculateMean = (data: number[]): number => {
    return data.reduce((sum, val) => sum + val, 0) / data.length;
  };

  const calculateMedian = (data: number[]): number => {
    const sorted = [...data].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
  };

  const calculateMode = (data: number[]): number[] => {
    const frequency: { [key: number]: number } = {};
    data.forEach(val => frequency[val] = (frequency[val] || 0) + 1);
    
    const maxFreq = Math.max(...Object.values(frequency));
    if (maxFreq === 1) return []; // No mode if all values appear once
    
    return Object.keys(frequency)
      .filter(key => frequency[parseFloat(key)] === maxFreq)
      .map(key => parseFloat(key));
  };

  const calculateQuartiles = (data: number[]): { q1: number; q3: number } => {
    const sorted = [...data].sort((a, b) => a - b);
    const n = sorted.length;
    
    const q1Index = Math.floor(n / 4);
    const q3Index = Math.floor(3 * n / 4);
    
    const q1 = n % 4 === 0 
      ? (sorted[q1Index - 1] + sorted[q1Index]) / 2
      : sorted[q1Index];
    
    const q3 = n % 4 === 0
      ? (sorted[q3Index - 1] + sorted[q3Index]) / 2
      : sorted[q3Index];
    
    return { q1, q3 };
  };

  const calculateVariance = (data: number[], mean: number): number => {
    return data.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / data.length;
  };

  const calculateStandardDeviation = (variance: number): number => {
    return Math.sqrt(variance);
  };

  const calculateSkewness = (data: number[], mean: number, stdDev: number): number => {
    const n = data.length;
    const skew = data.reduce((sum, val) => sum + Math.pow((val - mean) / stdDev, 3), 0);
    return skew / n;
  };

  const calculateKurtosis = (data: number[], mean: number, stdDev: number): number => {
    const n = data.length;
    const kurt = data.reduce((sum, val) => sum + Math.pow((val - mean) / stdDev, 4), 0);
    return (kurt / n) - 3; // Excess kurtosis
  };

  const calculateCorrelation = (x: number[], y: number[]): number => {
    if (x.length !== y.length) return 0;
    
    const n = x.length;
    const meanX = calculateMean(x);
    const meanY = calculateMean(y);
    
    const numerator = x.reduce((sum, xi, i) => sum + (xi - meanX) * (y[i] - meanY), 0);
    const denomX = Math.sqrt(x.reduce((sum, xi) => sum + Math.pow(xi - meanX, 2), 0));
    const denomY = Math.sqrt(y.reduce((sum, yi) => sum + Math.pow(yi - meanY, 2), 0));
    
    return numerator / (denomX * denomY);
  };

  const calculateLinearRegression = (x: number[], y: number[]): { slope: number; intercept: number; rSquared: number } => {
    if (x.length !== y.length) return { slope: 0, intercept: 0, rSquared: 0 };
    
    const n = x.length;
    const meanX = calculateMean(x);
    const meanY = calculateMean(y);
    
    const numerator = x.reduce((sum, xi, i) => sum + (xi - meanX) * (y[i] - meanY), 0);
    const denominator = x.reduce((sum, xi) => sum + Math.pow(xi - meanX, 2), 0);
    
    const slope = numerator / denominator;
    const intercept = meanY - slope * meanX;
    
    // Calculate R-squared
    const correlation = calculateCorrelation(x, y);
    const rSquared = Math.pow(correlation, 2);
    
    return { slope, intercept, rSquared };
  };

  const detectOutliers = (data: number[]): number[] => {
    const sorted = [...data].sort((a, b) => a - b);
    const { q1, q3 } = calculateQuartiles(data);
    const iqr = q3 - q1;
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;
    
    return data.filter(val => val < lowerBound || val > upperBound);
  };

  const calculateFrequencies = (data: number[]): { [key: string]: number } => {
    const freq: { [key: string]: number } = {};
    data.forEach(val => {
      const key = val.toString();
      freq[key] = (freq[key] || 0) + 1;
    });
    return freq;
  };

  const getCorrelationStrength = (r: number): string => {
    const abs = Math.abs(r);
    if (abs >= 0.9) return 'Very Strong';
    if (abs >= 0.7) return 'Strong';
    if (abs >= 0.5) return 'Moderate';
    if (abs >= 0.3) return 'Weak';
    return 'Very Weak';
  };

  const performCalculation = useCallback((): StatisticsResult | null => {
    const startTime = performance.now();
    
    try {
      const data = parseData(inputData);
      if (data.length === 0) return null;

      const data2 = inputData2 ? parseData(inputData2) : [];
      const decimals = parseInt(decimalPlaces) || 2;

      const mean = calculateMean(data);
      const median = calculateMedian(data);
      const mode = calculateMode(data);
      const min = Math.min(...data);
      const max = Math.max(...data);
      const range = max - min;
      const { q1, q3 } = calculateQuartiles(data);
      const iqr = q3 - q1;

      const variance = calculateVariance(data, mean);
      const standardDeviation = calculateStandardDeviation(variance);
      const coefficientOfVariation = (standardDeviation / mean) * 100;
      const meanAbsoluteDeviation = data.reduce((sum, val) => sum + Math.abs(val - mean), 0) / data.length;

      const skewness = calculateSkewness(data, mean, standardDeviation);
      const kurtosis = calculateKurtosis(data, mean, standardDeviation);
      const outliers = detectOutliers(data);
      const isNormal = Math.abs(skewness) < 1 && Math.abs(kurtosis) < 1;

      let correlation, regression;
      if (data2.length > 0 && data2.length === data.length) {
        const corrCoeff = calculateCorrelation(data, data2);
        correlation = {
          dataset2: data2,
          coefficient: corrCoeff,
          determination: Math.pow(corrCoeff, 2),
          strength: getCorrelationStrength(corrCoeff)
        };

        const regResult = calculateLinearRegression(data, data2);
        regression = {
          slope: regResult.slope,
          intercept: regResult.intercept,
          equation: `y = ${regResult.slope.toFixed(decimals)}x + ${regResult.intercept.toFixed(decimals)}`,
          rSquared: regResult.rSquared
        };
      }

      const frequencies = showFrequencies ? calculateFrequencies(data) : undefined;

      const endTime = performance.now();
      const processingTime = endTime - startTime;

      return {
        dataset: data,
        method: analysisMethod,
        count: data.length,
        processingTime,
        timestamp: new Date(),
        descriptive: showDescriptive ? {
          mean: parseFloat(mean.toFixed(decimals)),
          median: parseFloat(median.toFixed(decimals)),
          mode,
          range: parseFloat(range.toFixed(decimals)),
          min,
          max,
          q1: parseFloat(q1.toFixed(decimals)),
          q3: parseFloat(q3.toFixed(decimals)),
          iqr: parseFloat(iqr.toFixed(decimals))
        } : undefined,
        variability: showVariability ? {
          variance: parseFloat(variance.toFixed(decimals)),
          standardDeviation: parseFloat(standardDeviation.toFixed(decimals)),
          coefficientOfVariation: parseFloat(coefficientOfVariation.toFixed(decimals)),
          meanAbsoluteDeviation: parseFloat(meanAbsoluteDeviation.toFixed(decimals))
        } : undefined,
        distribution: showDistribution ? {
          skewness: parseFloat(skewness.toFixed(decimals)),
          kurtosis: parseFloat(kurtosis.toFixed(decimals)),
          isNormal,
          outliers
        } : undefined,
        correlation,
        regression,
        frequencies
      };
    } catch (error) {
      return null;
    }
  }, [inputData, inputData2, analysisMethod, showDescriptive, showVariability, showDistribution, showFrequencies, decimalPlaces]);

  const performAnalysis = useCallback((): void => {
    const statResult = performCalculation();
    if (statResult) {
      setResult(statResult);
      setHistory(prev => [statResult, ...prev.slice(0, 9)]);
      
      setStats(prev => ({
        calculationsPerformed: prev.calculationsPerformed + 1,
        largestDataset: Math.max(statResult.count, prev.largestDataset),
        totalTime: prev.totalTime + statResult.processingTime,
        averageTime: (prev.totalTime + statResult.processingTime) / (prev.calculationsPerformed + 1),
        lastMethod: statResult.method
      }));
    } else {
      setResult(null);
    }
  }, [performCalculation]);

  useEffect(() => {
    if (inputData.trim()) {
      performAnalysis();
    }
  }, [inputData, inputData2, analysisMethod, showDescriptive, showVariability, showDistribution, showFrequencies, decimalPlaces, performAnalysis]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    if (!result) return;
    
    let textToCopy = `Statistical Analysis Results\n`;
    textToCopy += `Dataset: ${result.dataset.join(', ')}\n`;
    textToCopy += `Sample Size: ${result.count}\n\n`;
    
    if (result.descriptive) {
      textToCopy += `Descriptive Statistics:\n`;
      textToCopy += `Mean: ${result.descriptive.mean}\n`;
      textToCopy += `Median: ${result.descriptive.median}\n`;
      textToCopy += `Mode: ${result.descriptive.mode.length > 0 ? result.descriptive.mode.join(', ') : 'None'}\n`;
      textToCopy += `Range: ${result.descriptive.range}\n`;
      textToCopy += `Q1: ${result.descriptive.q1}, Q3: ${result.descriptive.q3}\n`;
      textToCopy += `IQR: ${result.descriptive.iqr}\n\n`;
    }
    
    if (result.variability) {
      textToCopy += `Variability Measures:\n`;
      textToCopy += `Standard Deviation: ${result.variability.standardDeviation}\n`;
      textToCopy += `Variance: ${result.variability.variance}\n`;
      textToCopy += `Coefficient of Variation: ${result.variability.coefficientOfVariation}%\n\n`;
    }
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification('Results copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadResults = (): void => {
    if (!result) return;
    
    let content = `Statistical Analysis Report\n`;
    content += `${'='.repeat(50)}\n`;
    content += `Generated: ${result.timestamp.toLocaleString()}\n`;
    content += `Processing Time: ${result.processingTime.toFixed(2)}ms\n`;
    content += `Sample Size: ${result.count} values\n\n`;
    
    content += `Raw Data:\n${result.dataset.join(', ')}\n\n`;
    
    if (result.descriptive) {
      content += `Descriptive Statistics:\n`;
      content += `${'-'.repeat(25)}\n`;
      content += `Mean (Average): ${result.descriptive.mean}\n`;
      content += `Median (Middle): ${result.descriptive.median}\n`;
      content += `Mode (Most Frequent): ${result.descriptive.mode.length > 0 ? result.descriptive.mode.join(', ') : 'None'}\n`;
      content += `Minimum: ${result.descriptive.min}\n`;
      content += `Maximum: ${result.descriptive.max}\n`;
      content += `Range: ${result.descriptive.range}\n`;
      content += `First Quartile (Q1): ${result.descriptive.q1}\n`;
      content += `Third Quartile (Q3): ${result.descriptive.q3}\n`;
      content += `Interquartile Range (IQR): ${result.descriptive.iqr}\n\n`;
    }
    
    if (result.variability) {
      content += `Variability Measures:\n`;
      content += `${'-'.repeat(25)}\n`;
      content += `Standard Deviation: ${result.variability.standardDeviation}\n`;
      content += `Variance: ${result.variability.variance}\n`;
      content += `Coefficient of Variation: ${result.variability.coefficientOfVariation}%\n`;
      content += `Mean Absolute Deviation: ${result.variability.meanAbsoluteDeviation}\n\n`;
    }
    
    if (result.distribution) {
      content += `Distribution Analysis:\n`;
      content += `${'-'.repeat(25)}\n`;
      content += `Skewness: ${result.distribution.skewness}\n`;
      content += `Kurtosis: ${result.distribution.kurtosis}\n`;
      content += `Appears Normal: ${result.distribution.isNormal ? 'Yes' : 'No'}\n`;
      content += `Outliers: ${result.distribution.outliers.length > 0 ? result.distribution.outliers.join(', ') : 'None detected'}\n\n`;
    }
    
    if (result.correlation) {
      content += `Correlation Analysis:\n`;
      content += `${'-'.repeat(25)}\n`;
      content += `Correlation Coefficient: ${result.correlation.coefficient.toFixed(4)}\n`;
      content += `Coefficient of Determination (R²): ${result.correlation.determination.toFixed(4)}\n`;
      content += `Relationship Strength: ${result.correlation.strength}\n\n`;
    }
    
    if (result.regression) {
      content += `Linear Regression:\n`;
      content += `${'-'.repeat(25)}\n`;
      content += `Equation: ${result.regression.equation}\n`;
      content += `R-squared: ${result.regression.rSquared.toFixed(4)}\n\n`;
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'statistical-analysis.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Analysis downloaded!');
  };

  const clearAll = (): void => {
    setInputData('');
    setInputData2('');
    setDecimalPlaces('2');
    setShowDescriptive(true);
    setShowVariability(false);
    setShowDistribution(false);
    setShowFrequencies(false);
    setResult(null);
    setHistory([]);
    setStats({
      calculationsPerformed: 0,
      largestDataset: 0,
      totalTime: 0,
      averageTime: 0,
      lastMethod: ''
    });
    showNotification('Calculator reset!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleData = (): void => {
    setInputData('12, 15, 18, 20, 22, 25, 28, 30, 32, 35, 38, 40, 42, 45, 48');
    setAnalysisMethod('descriptive');
    setShowDescriptive(true);
    setShowVariability(true);
    setShowDistribution(true);
    showNotification('Sample data loaded!');
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-teal-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Statistics Calculator</h1>
              <p className="text-gray-300 text-sm">Comprehensive statistical analysis and data insights</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Analysis Method Selector */}
        <div className="mb-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Analysis Focus</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <button
              onClick={() => setAnalysisMethod('descriptive')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                analysisMethod === 'descriptive'
                  ? 'border-green-500 bg-green-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Descriptive</div>
              <div className="text-sm opacity-75">Mean, median, quartiles</div>
            </button>
            
            <button
              onClick={() => setAnalysisMethod('variability')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                analysisMethod === 'variability'
                  ? 'border-green-500 bg-green-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Variability</div>
              <div className="text-sm opacity-75">Standard deviation, variance</div>
            </button>
            
            <button
              onClick={() => setAnalysisMethod('distribution')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                analysisMethod === 'distribution'
                  ? 'border-green-500 bg-green-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Distribution</div>
              <div className="text-sm opacity-75">Skewness, outliers</div>
            </button>
            
            <button
              onClick={() => setAnalysisMethod('correlation')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                analysisMethod === 'correlation'
                  ? 'border-green-500 bg-green-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Correlation</div>
              <div className="text-sm opacity-75">Relationship analysis</div>
            </button>
            
            <button
              onClick={() => setAnalysisMethod('regression')}
              className={`p-4 rounded-lg border-2 transition-colors ${
                analysisMethod === 'regression'
                  ? 'border-green-500 bg-green-500/20 text-white'
                  : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
              }`}
            >
              <div className="font-medium">Regression</div>
              <div className="text-sm opacity-75">Linear modeling</div>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Calculation Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.calculationsPerformed}</div>
                <div className="text-gray-300 text-sm">Calculations</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.averageTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Avg Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-xl font-bold text-blue-400">{stats.largestDataset}</div>
                <div className="text-gray-300 text-sm">Largest Dataset</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-xl font-bold text-purple-400">{stats.lastMethod}</div>
                <div className="text-gray-300 text-sm">Last Method</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{history.length}</div>
                <div className="text-gray-300 text-sm">History</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Data Input</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadSampleData}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Zap className="w-4 h-4" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Dataset (comma, space, or line separated)
                </label>
                <textarea
                  value={inputData}
                  onChange={(e) => setInputData(e.target.value)}
                  placeholder="Enter your data: 1, 2, 3, 4, 5..."
                  rows={4}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                />
              </div>

              {(analysisMethod === 'correlation' || analysisMethod === 'regression') && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Second Dataset (for correlation/regression)
                  </label>
                  <textarea
                    value={inputData2}
                    onChange={(e) => setInputData2(e.target.value)}
                    placeholder="Enter second dataset: 2, 4, 6, 8, 10..."
                    rows={3}
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Decimal Places
                </label>
                <select
                  value={decimalPlaces}
                  onChange={(e) => setDecimalPlaces(e.target.value)}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
                >
                  <option value="0">0 (whole numbers)</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </select>
              </div>

              <div className="border-t border-gray-600 pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Descriptive Statistics
                  </label>
                  <button
                    onClick={() => setShowDescriptive(!showDescriptive)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showDescriptive ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showDescriptive ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Variability Measures
                  </label>
                  <button
                    onClick={() => setShowVariability(!showVariability)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showVariability ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showVariability ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Distribution Analysis
                  </label>
                  <button
                    onClick={() => setShowDistribution(!showDistribution)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showDistribution ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showDistribution ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-300">
                    Frequency Distribution
                  </label>
                  <button
                    onClick={() => setShowFrequencies(!showFrequencies)}
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                      showFrequencies ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                    }`}
                  >
                    {showFrequencies ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Statistical Analysis</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!result}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadResults}
                  disabled={!result}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto min-h-[500px]">
              {result ? (
                <div className="space-y-4">
                  <div className="text-center border-b border-gray-600 pb-4">
                    <div className="text-2xl font-bold text-green-400 mb-2">
                      {result.count} Data Points Analyzed
                    </div>
                    <div className="text-gray-400 text-sm">
                      Method: {result.method} | 
                      Time: {result.processingTime.toFixed(2)}ms |
                      Generated: {result.timestamp.toLocaleString()}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-white font-medium mb-2">Dataset:</h4>
                    <div className="bg-gray-700 p-3 rounded font-mono text-sm text-green-400 max-h-20 overflow-y-auto">
                      {result.dataset.join(', ')}
                    </div>
                  </div>
                  
                  {result.descriptive && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Descriptive Statistics:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <div className="text-blue-400">Mean: <span className="text-white font-mono">{result.descriptive.mean}</span></div>
                            <div className="text-blue-400">Median: <span className="text-white font-mono">{result.descriptive.median}</span></div>
                            <div className="text-blue-400">Mode: <span className="text-white font-mono">{result.descriptive.mode.length > 0 ? result.descriptive.mode.join(', ') : 'None'}</span></div>
                            <div className="text-blue-400">Range: <span className="text-white font-mono">{result.descriptive.range}</span></div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-yellow-400">Min: <span className="text-white font-mono">{result.descriptive.min}</span></div>
                            <div className="text-yellow-400">Max: <span className="text-white font-mono">{result.descriptive.max}</span></div>
                            <div className="text-yellow-400">Q1: <span className="text-white font-mono">{result.descriptive.q1}</span></div>
                            <div className="text-yellow-400">Q3: <span className="text-white font-mono">{result.descriptive.q3}</span></div>
                            <div className="text-yellow-400">IQR: <span className="text-white font-mono">{result.descriptive.iqr}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {result.variability && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Variability Measures:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <div className="text-purple-400">Standard Deviation: <span className="text-white font-mono">{result.variability.standardDeviation}</span></div>
                            <div className="text-purple-400">Variance: <span className="text-white font-mono">{result.variability.variance}</span></div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-pink-400">Coefficient of Variation: <span className="text-white font-mono">{result.variability.coefficientOfVariation}%</span></div>
                            <div className="text-pink-400">Mean Absolute Deviation: <span className="text-white font-mono">{result.variability.meanAbsoluteDeviation}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {result.distribution && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Distribution Analysis:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <div className="text-orange-400">Skewness: <span className="text-white font-mono">{result.distribution.skewness}</span></div>
                            <div className="text-orange-400">Kurtosis: <span className="text-white font-mono">{result.distribution.kurtosis}</span></div>
                          </div>
                          <div className="space-y-1">
                            <div className="text-teal-400">Normal Distribution: <span className="text-white font-mono">{result.distribution.isNormal ? 'Yes' : 'No'}</span></div>
                            <div className="text-teal-400">Outliers: <span className="text-white font-mono">{result.distribution.outliers.length > 0 ? result.distribution.outliers.join(', ') : 'None'}</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {result.correlation && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Correlation Analysis:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1">
                        <div className="text-cyan-400">Correlation Coefficient (r): <span className="text-white font-mono">{result.correlation.coefficient.toFixed(4)}</span></div>
                        <div className="text-cyan-400">Coefficient of Determination (R²): <span className="text-white font-mono">{result.correlation.determination.toFixed(4)}</span></div>
                        <div className="text-cyan-400">Relationship Strength: <span className="text-white font-mono">{result.correlation.strength}</span></div>
                        <div className="text-gray-400 text-xs mt-2">
                          Second Dataset: {result.correlation.dataset2.join(', ')}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {result.regression && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Linear Regression:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm space-y-1">
                        <div className="text-indigo-400">Equation: <span className="text-white font-mono text-lg">{result.regression.equation}</span></div>
                        <div className="text-indigo-400">Slope: <span className="text-white font-mono">{result.regression.slope.toFixed(4)}</span></div>
                        <div className="text-indigo-400">Y-Intercept: <span className="text-white font-mono">{result.regression.intercept.toFixed(4)}</span></div>
                        <div className="text-indigo-400">R-squared: <span className="text-white font-mono">{result.regression.rSquared.toFixed(4)}</span></div>
                      </div>
                    </div>
                  )}
                  
                  {result.frequencies && (
                    <div>
                      <h4 className="text-white font-medium mb-2">Frequency Distribution:</h4>
                      <div className="bg-gray-700 p-3 rounded text-sm max-h-32 overflow-y-auto">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {Object.entries(result.frequencies)
                            .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                            .map(([value, frequency]) => (
                            <div key={value} className="text-emerald-400 font-mono text-xs">
                              {value}: <span className="text-white">{frequency}×</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Enter data to perform statistical analysis</p>
                    <p className="text-sm mt-2">Input numbers separated by commas, spaces, or new lines</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Analyses</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-40 overflow-y-auto">
              {history.slice(0, 6).map((analysis, index) => (
                <div key={index} className="bg-gray-700 p-3 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-white">
                      {analysis.method} ({analysis.count} points)
                    </div>
                    <div className="text-xs text-gray-400">
                      {analysis.processingTime.toFixed(1)}ms
                    </div>
                  </div>
                  <div className="text-xs font-mono text-green-400 truncate">
                    {analysis.dataset.slice(0, 8).join(', ')}
                    {analysis.dataset.length > 8 ? '...' : ''}
                  </div>
                  {analysis.descriptive && (
                    <div className="text-xs text-blue-400 mt-1">
                      Mean: {analysis.descriptive.mean}
                    </div>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    {analysis.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Information Section */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Statistical Concepts</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-green-400 mb-2 flex items-center">
                <TrendingUp className="w-4 h-4 mr-1" />
                Descriptive Statistics
              </h4>
              <div className="text-gray-300 space-y-1">
                <div><strong>Mean:</strong> Average value of dataset</div>
                <div><strong>Median:</strong> Middle value when sorted</div>
                <div><strong>Mode:</strong> Most frequently occurring value</div>
                <div><strong>Quartiles:</strong> Values dividing data into quarters</div>
              </div>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-yellow-400 mb-2">Variability Measures</h4>
              <div className="text-gray-300 space-y-1">
                <div><strong>Standard Deviation:</strong> Average distance from mean</div>
                <div><strong>Variance:</strong> Square of standard deviation</div>
                <div><strong>CV:</strong> Relative variability as percentage</div>
                <div><strong>MAD:</strong> Mean absolute deviation from mean</div>
              </div>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-blue-400 mb-2">Distribution Analysis</h4>
              <div className="text-gray-300 space-y-1">
                <div><strong>Skewness:</strong> Asymmetry of distribution</div>
                <div><strong>Kurtosis:</strong> Tail heaviness of distribution</div>
                <div><strong>Outliers:</strong> Values far from typical range</div>
                <div><strong>Normality:</strong> Bell curve characteristics</div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-purple-400 mb-2">Correlation & Regression</h4>
              <div className="text-gray-300 space-y-1">
                <div><strong>Correlation:</strong> Strength of linear relationship (-1 to +1)</div>
                <div><strong>R-squared:</strong> Proportion of variance explained</div>
                <div><strong>Regression:</strong> Predicts Y from X using linear equation</div>
                <div><strong>Slope:</strong> Change in Y per unit change in X</div>
              </div>
            </div>
            <div className="bg-gray-700 p-3 rounded">
              <h4 className="font-medium text-red-400 mb-2">Applications</h4>
              <div className="text-gray-300 space-y-1">
                <div><strong>Research:</strong> Analyze experimental data</div>
                <div><strong>Business:</strong> Sales and performance metrics</div>
                <div><strong>Quality Control:</strong> Manufacturing standards</div>
                <div><strong>Finance:</strong> Risk assessment and forecasting</div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticsCalculator;